import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Spinner,
  Banner,
  DataTable,
  Badge,
  Box,
} from '@shopify/polaris';
import { ExitIcon } from '@shopify/polaris-icons';

import { useAdminAuth } from '../../providers/AdminAuthProvider';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, logout, adminFetch, username } = useAdminAuth();
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      const response = await adminFetch('/api/admin/dashboard');
      if (response.ok) {
        const data = await response.json();
        setDashboard(data.dashboard);
      } else {
        setError('Failed to load dashboard');
      }
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadDashboard();
  }, [isAuthenticated, navigate, loadDashboard]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  if (!isAuthenticated) {
    return null;
  }

  if (isLoading) {
    return (
      <Page title="Admin Dashboard">
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  const recentStoresRows = dashboard?.recent_stores?.map(store => [
    store.shop_domain,
    store.shop_name || '-',
    <Badge tone={store.plan_type === 'premium' ? 'success' : 'info'}>
      {store.plan_type}
    </Badge>,
    store.installed_at ? new Date(store.installed_at).toLocaleDateString() : '-',
  ]) || [];

  return (
    <Page
      title="Admin Dashboard"
      primaryAction={{
        content: 'Logout',
        icon: ExitIcon,
        onAction: handleLogout,
        destructive: true,
      }}
      secondaryActions={[
        { content: 'Stores', onAction: () => navigate('/admin/stores') },
        { content: 'Discounts', onAction: () => navigate('/admin/discounts') },
        { content: 'Analytics', onAction: () => navigate('/admin/analytics') },
        { content: 'Support', onAction: () => navigate('/admin/support') },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Text variant="bodyMd" tone="subdued">
            Logged in as: {username}
          </Text>
        </Layout.Section>

        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <div style={{ flex: 1 }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Total Stores</Text>
                  <Text variant="heading2xl">{dashboard?.stores?.total || 0}</Text>
                  <InlineStack gap="200">
                    <Badge tone="success">{dashboard?.stores?.active || 0} active</Badge>
                    <Badge tone="info">{dashboard?.stores?.premium || 0} premium</Badge>
                  </InlineStack>
                </BlockStack>
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Total Bars</Text>
                  <Text variant="heading2xl">{dashboard?.bars?.total || 0}</Text>
                  <Badge tone="success">{dashboard?.bars?.active || 0} active</Badge>
                </BlockStack>
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">New Stores (30d)</Text>
                  <Text variant="heading2xl">{dashboard?.stores?.new_last_30_days || 0}</Text>
                </BlockStack>
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Active Discounts</Text>
                  <Text variant="heading2xl">{dashboard?.discounts?.active || 0}</Text>
                </BlockStack>
              </Card>
            </div>
          </InlineStack>
        </Layout.Section>

        {/* Analytics Summary */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd">Global Analytics</Text>
              <InlineStack gap="800">
                <BlockStack gap="100">
                  <Text variant="bodyMd" tone="subdued">Total Impressions</Text>
                  <Text variant="headingLg">{dashboard?.analytics?.total_impressions?.toLocaleString() || 0}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodyMd" tone="subdued">Total Clicks</Text>
                  <Text variant="headingLg">{dashboard?.analytics?.total_clicks?.toLocaleString() || 0}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodyMd" tone="subdued">Total Conversions</Text>
                  <Text variant="headingLg">{dashboard?.analytics?.total_conversions?.toLocaleString() || 0}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodyMd" tone="subdued">Total Revenue</Text>
                  <Text variant="headingLg">${dashboard?.analytics?.total_revenue?.toLocaleString() || 0}</Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Recent Stores */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd">Recent Signups</Text>
                <Button variant="plain" onClick={() => navigate('/admin/stores')}>
                  View all stores
                </Button>
              </InlineStack>
              {recentStoresRows.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Domain', 'Name', 'Plan', 'Installed']}
                  rows={recentStoresRows}
                />
              ) : (
                <Text tone="subdued">No recent signups</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
